import React, { useMemo, useState } from 'react';
import { ThemeProvider, createTheme, CssBaseline, Container, Box } from '@mui/material';
import Header from './components/Header';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import Home from './pages/Home';
import Portfolio from './pages/Portfolio';
// import STLModels from './pages/STLModels'; // Hidden
import Contact from './pages/Contact';
import Admin from './pages/Admin';
import { LanguageProvider } from './contexts/LanguageContext';
import { AuthProvider } from './contexts/AuthContext';
import { CategoryProvider } from './contexts/CategoryContext';

const App: React.FC = () => {
  const [mode, setMode] = useState<'light' | 'dark'>('light');

  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode,
          primary: {
            main: '#2196f3', // Blue from the logo
            light: '#64b5f6',
            dark: '#1976d2',
          },
          secondary: {
            main: '#ff4081', // Pink/Red accent from the logo
            light: '#ff80ab',
            dark: '#f50057',
          },
          background: {
            default: mode === 'light' ? '#f8f9fa' : '#121212',
            paper: mode === 'light' ? '#ffffff' : '#1e1e1e',
          },
          text: {
            primary: mode === 'light' ? '#2c3e50' : '#ffffff',
            secondary: mode === 'light' ? '#546e7a' : '#b0bec5',
          },
        },
        components: {
          MuiContainer: {
            styleOverrides: {
              root: {
                paddingTop: '2rem',
                paddingBottom: '2rem',
              },
            },
          },
          MuiPaper: {
            styleOverrides: {
              root: {
                backgroundImage: 'none',
                borderRadius: 12,
              },
            },
          },
          MuiButton: {
            styleOverrides: {
              root: {
                borderRadius: 8,
                textTransform: 'none',
                fontWeight: 600,
              },
              contained: {
                boxShadow: 'none',
                '&:hover': {
                  boxShadow: '0px 2px 4px rgba(0,0,0,0.1)',
                },
              },
            },
          },
          MuiCard: {
            styleOverrides: {
              root: {
                borderRadius: 12,
                transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 8px 16px rgba(0,0,0,0.1)',
                },
              },
            },
          },
        },
        typography: {
          h1: {
            fontWeight: 700,
          },
          h2: {
            fontWeight: 700,
          },
          h3: {
            fontWeight: 600,
          },
          h4: {
            fontWeight: 600,
          },
          h5: {
            fontWeight: 600,
          },
          button: {
            fontWeight: 600,
          },
        },
      }),
    [mode]
  );

  const toggleTheme = () => {
    setMode((prev) => (prev === 'light' ? 'dark' : 'light'));
  };

  return (
    <LanguageProvider>
      <AuthProvider>
        <CategoryProvider>
          <ThemeProvider theme={theme}>
            <CssBaseline />
            <Router>
              <Box
                sx={{
                  minHeight: '100vh',
                  background: mode === 'light'
                    ? 'linear-gradient(135deg, #f8f9fa 0%, #e3f2fd 100%)'
                    : 'linear-gradient(135deg, #121212 0%, #1a237e 100%)',
                }}
              >
                <Header mode={mode} toggleTheme={toggleTheme} />
                <Container maxWidth="lg">
                  <Routes>
                    <Route path="/" element={<Home />} />
                    <Route path="/portfolio" element={<Portfolio />} />
                    {/* STL Models page hidden */}
                    {/* <Route path="/stl-models" element={<STLModels />} /> */}
                    <Route path="/contact" element={<Contact />} />
                    <Route path="/admin" element={<Admin />} />
                  </Routes>
                </Container>
              </Box>
            </Router>
          </ThemeProvider>
        </CategoryProvider>
      </AuthProvider>
    </LanguageProvider>
  );
};

export default App; 